import { isEmailInDomains } from '@order-management/roles';
import { formatSalesOrderNumber } from '@order-management/format';
import {
  NewUserAccess,
  NewUserAccessPermissionsGroupUserRoleAssignment,
  SalesOrdersAccessTableRow,
} from '@order-management/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Section } from 'react-bulma-components';
import { FaCheck } from 'react-icons/fa6';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { environment } from '../../../environments/environment';
import CancelAlertModal from '../../shared/cancel-alert-modal/cancel-alert-modal';
import useHttpRequest from '../../shared/hooks/http-request';
import { isEmail } from '../../shared/utils';
import Button from '../../ui/button/button';
import CustomerAccessSetupConfirmationModal from '../admin-customer-access-setup-confirmation-modal/admin-customer-access-setup-confirmation-modal';
import {
  CustomerAccessSetupPageContext,
  CustomerAccessSetupSteps,
} from '../admin-customer-access-setup-page/types';
import {
  grantCustomerAccess,
  validateCustomerAccessGranting,
} from '../admin-service';
import { SalesOrdersAccessStorageService } from '../sales-orders-access-storage-service';
import {
  InviteCustomerUserDetailsForm,
  InviteCustomerUserDetailsFormData,
  InviteCustomerUserDetailsFormValidation,
} from './invite-customer-access-user-details-form/invite-customer-access-user-details-form';

const customerAccessSalesOrdersSessionStorageService =
  new SalesOrdersAccessStorageService();

const INVALID_EMAIL_ERROR = 'Invalid format';
const CANCEL_BUTTON_TEXT = 'Cancel';
const SUBMIT_BUTTON_TEXT = 'Submit';
const CANCEL_ALERT_MODAL_TITLE = 'Cancel add customer user';
const CANCEL_ALERT_MODAL_SUBTITLE =
  'Are you sure you want to cancel the customer invitation process?';
const GRANT_PERMISSIONS_ERROR =
  'An error occurred while granting the selected permissions to the user';

export function AdminCustomerAccessUserDetailsPage() {
  const navigate = useNavigate();
  const { setStep, setHeaderButton } =
    useOutletContext<CustomerAccessSetupPageContext>();
  const {
    executeHttpRequest: executeValidateCustomerAccessGranting,
    isLoading: isValidatingCustomerAccessGranting,
  } = useHttpRequest();
  const {
    executeHttpRequest: executeGrantCustomerAccess,
    isLoading: isGrantingCustomerAccess,
  } = useHttpRequest();
  const [submitButtonPressed, setSubmitButtonPressed] =
    useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [showCancelAlertModal, setShowCancelAlertModal] =
    useState<boolean>(false);
  const [conflictingAssignments, setConflictingAssignments] =
    useState<NewUserAccessPermissionsGroupUserRoleAssignment[]>();
  const [assignmentsSalesOrders, setAssignmentsSalesOrders] = useState<
    SalesOrdersAccessTableRow[]
  >(customerAccessSalesOrdersSessionStorageService.get());

  const allowedInternalAndPartnerEmailDomains = useMemo(
    () =>
      environment.internalUserEmailDomains
        .split(',')
        .concat(environment.partnerUserEmailDomains.split(',')),
    []
  );

  const initialFormData: InviteCustomerUserDetailsFormData = useMemo(() => {
    return {
      email: { value: '', touched: false, error: null },
    };
  }, []);

  const [formData, setFormData] =
    useState<InviteCustomerUserDetailsFormData>(initialFormData);

  const validateForm: InviteCustomerUserDetailsFormValidation = useMemo(
    () => ({
      email: (value: string) => {
        if (!value || !isEmail(value)) {
          return INVALID_EMAIL_ERROR;
        }
        return null;
      },
    }),
    []
  );

  const validateData = useCallback(() => {
    return Object.keys(formData).reduce(
      (acc, key) => {
        const formDataKey = key as keyof InviteCustomerUserDetailsFormData;
        const error =
          validateForm[formDataKey]?.(formData[formDataKey].value) ?? null;
        setFormData((prev) => ({
          ...prev,
          [formDataKey]: { ...prev[formDataKey], error, touched: true },
        }));
        return {
          values: { ...acc.values, [key]: formData[formDataKey].value },
          errors: { ...acc.errors, [key]: error },
        };
      },
      { values: {}, errors: {} }
    );
  }, [formData, validateForm]);

  const handleGrantCustomerAccess = useCallback(async () => {
    executeGrantCustomerAccess({
      asyncFunction: async () => {
        const conflictingAssignmentsMap = new Map(
          conflictingAssignments?.map((assignment) => [
            assignment.salesOrder.id,
            assignment,
          ])
        );
        const salesOrdersToAssign = assignmentsSalesOrders.filter((so) => {
          const conflictingAssignment = conflictingAssignmentsMap.get(so.id);
          // Omit non overridable assignments
          return !conflictingAssignment || conflictingAssignment.overridable;
        });
        const newUserAccess: NewUserAccess = {
          email: formData.email.value,
          salesOrders: salesOrdersToAssign.map((so) => so.id),
        };
        setAssignmentsSalesOrders(salesOrdersToAssign);
        await grantCustomerAccess(newUserAccess);
      },
      customErrorMessage: GRANT_PERMISSIONS_ERROR,
    });
  }, [
    assignmentsSalesOrders,
    conflictingAssignments,
    executeGrantCustomerAccess,
    formData.email.value,
  ]);

  const handleSubmit = useCallback(
    async () =>
      executeValidateCustomerAccessGranting({
        asyncFunction: async () => {
          setSubmitButtonPressed(true);
          const formValidation = validateData();
          const isValidForm = Object.values(formValidation.errors).every(
            (fieldError) => !fieldError
          );
          if (isValidForm) {
            if (
              !isEmailInDomains(
                formData.email.value,
                allowedInternalAndPartnerEmailDomains
              )
            ) {
              const newUserAccess: NewUserAccess = {
                email: formData.email.value,
                salesOrders: assignmentsSalesOrders.map((so) => so.id),
              };
              const newAccessConflictsData =
                await validateCustomerAccessGranting(newUserAccess);
              if (newAccessConflictsData.length > 0) {
                setConflictingAssignments(newAccessConflictsData);
              } else {
                handleGrantCustomerAccess();
              }
            }
            setShowConfirmationModal(true);
          }
        },
        customErrorMessage: GRANT_PERMISSIONS_ERROR,
      }),
    [
      allowedInternalAndPartnerEmailDomains,
      assignmentsSalesOrders,
      executeValidateCustomerAccessGranting,
      formData.email.value,
      handleGrantCustomerAccess,
      validateData,
    ]
  );

  const handleAddAnotherUser = useCallback(() => {
    setFormData(initialFormData);
    setShowConfirmationModal(false);
    setSubmitButtonPressed(false);
    setConflictingAssignments(undefined);
    setAssignmentsSalesOrders(
      customerAccessSalesOrdersSessionStorageService.get()
    );
  }, [initialFormData]);

  const handleCloseConfirmationModal = useCallback(() => {
    setConflictingAssignments(undefined);
    setSubmitButtonPressed(false);
    setShowConfirmationModal(false);
  }, []);

  const navigateToAccessManagementPage = useCallback(async () => {
    navigate('/admin/temporary-access');
  }, [navigate]);

  const navigateToTemporaryAccessSetupPage = useCallback(async () => {
    navigate('/admin/temporary-access/setup/sales-orders');
  }, [navigate]);

  useEffect(() => {
    setStep(CustomerAccessSetupSteps.USER);
    setHeaderButton(undefined);
  }, [setHeaderButton, setStep]);

  return (
    <>
      <Section paddingless>
        <InviteCustomerUserDetailsForm
          data={formData}
          onChangeData={setFormData}
          validate={validateForm}
          submitButtonPressed={submitButtonPressed}
        />
      </Section>
      <Section
        paddingless
        display="flex"
        justifyContent={'flex-end'}
        alignItems={'flex-end'}
        className="gap-6 is-flex-grow-1 pt-0 pl-4"
      >
        <Button
          color="primary-dark"
          outlined
          onClick={() => setShowCancelAlertModal(true)}
        >
          {CANCEL_BUTTON_TEXT}
        </Button>
        <Button
          color="primary-dark"
          onClick={handleSubmit}
          isExecutingAction={
            isValidatingCustomerAccessGranting || isGrantingCustomerAccess
          }
        >
          <Button.LoadingIcon icon={FaCheck} />
          <span>{SUBMIT_BUTTON_TEXT}</span>
        </Button>
      </Section>

      <CustomerAccessSetupConfirmationModal
        show={showConfirmationModal}
        email={formData.email.value}
        assignmentsSalesOrdersNumbers={assignmentsSalesOrders.map((so) =>
          formatSalesOrderNumber(so.number)
        )}
        conflictingAssignments={conflictingAssignments}
        onAddAnotherUser={handleAddAnotherUser}
        onConfirm={handleGrantCustomerAccess}
        onClose={handleCloseConfirmationModal}
        navigateToTemporaryAccessDashboardPage={navigateToAccessManagementPage}
        navigateToTemporaryAccessSetupPage={navigateToTemporaryAccessSetupPage}
        loading={isGrantingCustomerAccess}
      />
      <CancelAlertModal
        show={showCancelAlertModal}
        title={CANCEL_ALERT_MODAL_TITLE}
        subtitle={CANCEL_ALERT_MODAL_SUBTITLE}
        onCancel={navigateToAccessManagementPage}
        onClose={() => setShowCancelAlertModal(false)}
      />
    </>
  );
}

export default AdminCustomerAccessUserDetailsPage;
